<template>
  <div class="right-of-cancellation-form">
    <template v-if="!hasSent">
      <validation-observer
        ref="observer"
        tag="form"
        class="inputs-container"
        @submit.prevent="validateForm()">
        <!-- Order Number -->
        <validation-provider
          rules="required"
          slim
          mode="passive">
          <template slot-scope="{ errors }">
            <oie-base-input-v2
              v-model="orderNumber"
              required
              maxlength="50"
              :label="tStatic('rightOfCancellationOrderNumber')"
              :placeholder="tStatic('rightOfCancellationOrderNumberPlaceholder')"
              :errors="errors"></oie-base-input-v2>
          </template>
        </validation-provider>

        <!-- Customer Number (Optional) -->
        <validation-provider
          rules=""
          slim
          mode="passive">
          <template slot-scope="{ errors }">
            <oie-base-input-v2
              v-model="customerNumber"
              maxlength="50"
              :label="tStatic('rightOfCancellationCustomerNumber')"
              :placeholder="tStatic('rightOfCancellationCustomerNumberPlaceholder')"
              :errors="errors"></oie-base-input-v2>
          </template>
        </validation-provider>

        <!-- Customer Email -->
        <validation-provider
          rules="required|email"
          slim
          mode="passive">
          <template slot-scope="{ errors }">
            <oie-base-input-v2
              v-model="customerEmail"
              required
              maxlength="100"
              :label="tStatic('rightOfCancellationCustomerEmail')"
              :placeholder="tStatic('rightOfCancellationCustomerEmailPlaceholder')"
              :errors="errors"></oie-base-input-v2>
          </template>
        </validation-provider>

        <!-- Cancellation Type Checkboxes -->
        <div class="cancellation-options">
          <label class="checkbox-container">
            <input
              v-model="cancellationOptions"
              type="checkbox"
              value="whole-order">
            <span>{{ tStatic('rightOfCancellationWholeOrder') }}</span>
          </label>
          <label class="checkbox-container">
            <input
              v-model="cancellationOptions"
              type="checkbox"
              value="single-item">
            <span>{{ tStatic('rightOfCancellationSingleItem') }}</span>
          </label>
        </div>

        <!-- Cancellation Reason -->
        <validation-provider
          rules="required"
          mode="passive"
          slim>
          <template slot-scope="{ errors }">
            <oie-base-textarea
              v-model="cancellationReason"
              maxlength="500"
              :label="tStatic('rightOfCancellationReason')"
              :placeholder="tStatic('rightOfCancellationReasonPlaceholder')"
              :errors="errors"></oie-base-textarea>
          </template>
        </validation-provider>

        <!-- reCAPTCHA -->
        <validation-provider
          v-slot="{ errors }"
          rules="required"
          mode="passive"
          slim>
          <oie-recaptcha-v2
            ref="recaptcha"
            v-model="recaptchaResponse"
            class="right-of-cancellation-form__recaptcha"
            :errors="errors">
          </oie-recaptcha-v2>
        </validation-provider>

        <!-- Submit Button -->
        <oie-base-action
          v-element-loader="{ endpoint: 'right-of-cancellation/submit' }"
          theme="secondary"
          type="submit"
          size="large">
          {{ tStatic('rightOfCancellationSubmitButton') }}
        </oie-base-action>
      </validation-observer>
    </template>

    <!-- Success Message -->
    <template v-else>
      <h2 class="right-of-cancellation-form__sent-title">
        {{ tStatic('rightOfCancellationSentTitle') }}
      </h2>
      <div class="right-of-cancellation-form__sent-text" v-html="tStatic('rightOfCancellationSentText')"></div>
      <oie-base-action
        type="button"
        theme="secondary"
        size="large"
        @click="hasSent = false">
        {{ tStatic('sendNewCancellationRequest') }}
      </oie-base-action>
    </template>
  </div>
</template>

<script>
import httpService from '@/core/http-service'

export default {
  props: {
    blockId: {
      type: String,
      default: '0'
    }
  },
  data: () => ({
    orderNumber: null,
    customerEmail: null,
    customerNumber: null,
    cancellationReason: null,
    cancellationOptions: [],
    hasSent: false,
    recaptchaResponse: null
  }),
  methods: {
    async validateForm() {
      const isValid = await this.$refs.observer.validate()
      if (isValid) {
        this.send()
      }
    },
    async send() {
      try {
        const payload = {
          orderNumber: this.orderNumber,
          customerEmail: this.customerEmail,
          customerNumber: this.customerNumber,
          cancellationReason: this.cancellationReason,
          cancellationType: this.cancellationOptions.join(','),
          recaptchaResponse: this.recaptchaResponse
        }

        const response = await httpService.post('right-of-cancellation/submit', payload)

        if (this.$refs.recaptcha) {
          this.$refs.recaptcha.reset()
        }

        // Check for notifications (errors/warnings from the server)
        if (response.data.notifications && response.data.notifications.length > 0) {
          const errorNotification = response.data.notifications.find(n => n.type === 'Error')
          if (errorNotification) {
            // Show error message to user
            this.$store.commit('addNotification', {
              message: errorNotification.message,
              type: 'error'
            })
            return
          }
        }

        // Success: clear form and show success message
        if (response && response.data) {
          this.orderNumber = ''
          this.customerEmail = ''
          this.customerNumber = ''
          this.cancellationReason = ''
          this.cancellationOptions = []
          this.recaptchaResponse = null
          this.hasSent = true
        } else {
          // No error notification but also no data = unexpected response
          this.$store.commit('addNotification', {
            message: 'An unexpected error occurred. Please try again.',
            type: 'error'
          })
        }
      } catch (error) {
        // Network error or other exception
        this.$store.commit('addNotification', {
          message: 'Failed to submit cancellation request. Please try again later.',
          type: 'error'
        })
      }
    }
  }
}
</script>

<style lang="scss">
.right-of-cancellation-form {
  position: relative;
  padding: measure(half);
  border: 1px solid color(border-gray);
  background-color: color(background-white);
}

.right-of-cancellation-form .inputs-container {
  max-width: 31.25rem;
}

.right-of-cancellation-form__sent-title {
  @include h3-semi-bold;
}

.right-of-cancellation-form__sent-text {
  margin-bottom: 2rem;
}

.right-of-cancellation-form__recaptcha {
  margin-bottom: 1rem;
}

.cancellation-options {
  margin-bottom: 1.5rem;
}

.checkbox-container {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;

  input[type="checkbox"] {
    margin-right: 0.5rem;
    cursor: pointer;
  }

  span {
    user-select: none;
  }
}
</style>
